<template>
  <v-layout column v-if="!loading" class="nx-entity-metadata">
    <h1 v-if="title && showHeading" class="title text-clamp">
      <v-icon color="black" size="24" class="mr-2">info_outline</v-icon>
      {{ title }}
    </h1>
    <nx-table :scopes="scopes" v-slot="{ items, scope }">
      <template v-for="(item, index) in items">
        <template v-if="item.type === 'key-value'">
          <nx-table-item :key="`${scope}-${index}`"
                         :title="translate(item.key)"
                         title-clamp
                         :subtitle="item.value"
                         :actionable="isActionable(item)"
                         :icon="item.copyToClipboard ? 'content_copy' : null"
                         @click="handleAction(item)"/>
        </template>
        <template v-if="item.type === 'link'">
          <nx-table-item :key="`${scope}-${index}`"
                         :title="item.text"
                         title-clamp
                         subtitle-clamp
                         :subtitle="item.url"
                         :actionable="isActionable(item)"
                         icon="arrow_forward"
                         @click="handleAction(item)"/>
        </template>
      </template>
    </nx-table>
  </v-layout>
  <v-layout column v-else class="ml-2 mt-3">
    <skeleton-loader class="title ml-2" width="80%" :height="20" animation="fade"/>
    <skeleton-loader class="scope mt-3 ml-2" width="40%" :height="20" animation="fade"/>
    <skeleton-loader class="ml-2 mt-2" width="80%" :height="300" animation="fade"/>
    <skeleton-loader class="scope mt-3 ml-2" width="40%" :height="20" animation="fade"/>
    <skeleton-loader class="ml-2 mt-2" width="80%" :height="200" animation="fade"/>
  </v-layout>
</template>

<script>
import apiFactory from '../api';
import SkeletonLoader from '@/components/skeleton/SkeletonLoader';
import NxTableItem from '@/components/table/TableItem';
import NxTable from '@/components/table/Table';
import { translateCandidates, copyToClipboard } from '@/utils';

export default {
  name: 'nx-entity-metadata',
  components: {
    NxTable,
    NxTableItem,
    SkeletonLoader,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    showHeading: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  data() {
    return {
      loading: true,
      title: null,
      scopes: [],
    };
  },
  methods: {
    reset() {
      this.title = null;
      this.scopes = [];
      this.loading = true;
    },
    async init() {
      try {
        const api = apiFactory(this.type);
        const { title, scopes } = await api.moreInfo(this.id);
        this.title = title;
        this.scopes = scopes;
        this.loading = false;
      } catch (err) {
        console.error(err);
        this.toast.notify('message.error.serverError');
      }
    },
    translate(translatable) {
      return translateCandidates(
        [
          `message.metadata.${translatable}`,
          translatable,
        ],
        translatable,
      );
    },
    isActionable(item) {
      if (item.type === 'link') {
        return true;
      }
      if (item.copyToClipboard === true) {
        return true;
      }
      return false;
    },
    async handleAction(item) {
      if (!this.isActionable(item)) {
        return;
      }

      if (item.type === 'link') {
        window.open(item.url);
        return;
      }
      if (item.copyToClipboard) {
        await copyToClipboard(item.value.toString());
        this.toast.notify('message.list.actions.clipboardCopy');
      }
    },
  },
  watch: {
    type() {
      this.reset();
      this.init();
    },
    id() {
      this.reset();
      this.init();
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="stylus" scoped>
.nx-entity-metadata {
  padding: 20px 20px 0;

  .title {
    font-size: 20px;
    font-weight: normal;
    color: black;
    margin-bottom: 8px;
  }
}
</style>
